// stim-sim-v0 reader: a static field on a subject's own cortex (TMS from SimNIBS's FEM, on its native
// head). `e_field` `[pose][vertex][3]` and `e_magnitude` `[pose][vertex]` are in V/m, on the cortex's
// vertices in the order the pack's meshes keep (`surfaces` maps the spans to its structures). The bundle
// has one fixed colour range, never per pose. At fixed geometry the field is linear in the coil's current
// slew rate, so an intensity multiplier a gives a·E and |a|·|E|; it isn't percent motor threshold, and no
// colour implies an activation threshold.

import type { SimFiles } from './files.ts';
import type { StimCoil, StimHeader, StimPose, StimSurface } from './types.ts';

export class StimSim {
  private magnitudes: Promise<Float32Array> | null = null;

  private constructor(
    readonly files: SimFiles,
    readonly header: StimHeader,
  ) {}

  static async open(files: SimFiles, header: StimHeader): Promise<StimSim> {
    for (const k of ['vertices', 'triangles', 'e_magnitude'])
      if (!header.arrays?.[k]) throw new Error(`sims/${files.dir}: a stim-sim-v0 run without arrays.${k}`);
    return new StimSim(files, header);
  }

  /** The cortex's vertices, left then right (`hemisphere_offsets`). */
  get vertexCount(): number {
    return this.header.arrays.vertices!.shape[0]!;
  }

  /** The coil poses, in their own order (`index`): pose order is not time. */
  get poses(): StimPose[] {
    return [...(this.header.poses ?? [])].sort((a, b) => a.index - b.index);
  }

  /** The pose shown first: `display.default_pose`, else the first. */
  get defaultPose(): StimPose | null {
    const id = this.header.display?.default_pose;
    return this.poses.find((p) => p.id === id) ?? this.poses[0] ?? null;
  }

  /** The bundle's fixed colour range, V/m. */
  get range(): [number, number] | null {
    return this.header.display?.range ?? null;
  }

  get surfaces(): StimSurface[] {
    return this.header.surfaces ?? [];
  }

  /** The structure a cortex vertex belongs to, by the spans `surfaces` gives; null outside them. */
  structureOf(vertex: number): string | null {
    return this.surfaces.find((s) => vertex >= s.vertex_offset && vertex < s.vertex_offset + s.vertex_count)?.structure ?? null;
  }

  /** |E| at each cortex vertex for a pose (its `index`), V/m. */
  async magnitude(pose = this.defaultPose?.index ?? 0): Promise<Float32Array> {
    const d = this.header.arrays.e_magnitude!;
    const all = await (this.magnitudes ??= this.files.array(d).then((a) => (a instanceof Float32Array ? a : Float32Array.from(a))));
    const V = this.vertexCount;
    if (d.shape.length !== 2 || d.shape[1] !== V) throw new Error(`sims/${this.files.dir}: e_magnitude is [${d.shape.join(', ')}], not [poses, ${V}]`);
    if (!(pose >= 0 && pose < d.shape[0]!)) throw new Error(`sims/${this.files.dir}: no pose ${pose} in e_magnitude's ${d.shape[0]}`);
    return all.subarray(pose * V, (pose + 1) * V);
  }

  /** The highest |E| at a pose, V/m, and the vertex it's at. */
  async peak(pose?: number): Promise<{ value: number; vertex: number }> {
    const m = await this.magnitude(pose);
    let vertex = -1;
    let value = -Infinity;
    for (let i = 0; i < m.length; i++) if (m[i]! > value) [value, vertex] = [m[i]!, i];
    return { value, vertex };
  }

  /** The coil (`files.coil`): its model, source and electrical model, for Methods. */
  async coil(): Promise<StimCoil | null> {
    const rel = this.header.files?.coil;
    return typeof rel === 'string' ? this.files.maybeJson<StimCoil>(rel) : null;
  }
}
